import Button from '@rio-cloud/rio-uikit/lib/es/components/button/Button';
import Collapse from '@rio-cloud/rio-uikit/lib/es/components/collapse/Collapse';
import { ChangeEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useIntl from 'react-intl/src/components/useIntl';
import { ColumnDescriptorProps, ListTable } from '../../../../../components/table/ListTable';
import { useGetAllWhitelistedDevDevicesQuery, useWhitelistDevDeviceMutation }
from '../../../../../services/devDeviceWhitelistApi';


export interface DevDeviceWhitelist {
    imsi: string;
    requester?: string;
    reason: string;
    timestamp?: string;
}

export const DevDeviceWhitelistList = () => {
    const { formatMessage } = useIntl();
    const { data: devDevicesWhitelistList = [], isLoading: isLoadingDevDevicesWhitelistList,
        error: errorDevDevicesWhitelistList } = useGetAllWhitelistedDevDevicesQuery();
    const [whitelistDevDevice, { isLoading: isLoadingDevDeviceWhitelistSubmit,
        isError, error: errorDevDevicesWhitelist }] = useWhitelistDevDeviceMutation();
    const [expanderIsOpen, setExpanderIsOpen] = useState(false);
    const [formData, setFormData] = useState<{
        imsi?: string;
        reason?: string;
    }>(() => ({
        imsi: '',
        reason: '',
    }));
    const [errors, setErrors] = useState<{
        imsi?: string;
        reason?: string;
    }>({});

    const columnDescriptors: ColumnDescriptorProps<DevDeviceWhitelist>[] = [
        {
            id: 'imsi',
            field: 'imsi',
            label: 'IMSI',
            format: (field) => <span>{field}</span>,
        },
        {
            id: 'requester',
            field: 'requester',
            label: 'Requester',
            format: (field) => <span>{field}</span>,
        },
        {
            id: 'reason',
            field: 'reason',
            label: 'Reason',
            format: (field) => <span>{field}</span>,
        },
        {
            id: 'timestamp',
            field: 'timestamp',
            label: 'Creation Date',
            format: (field) => <span>{field}</span>,
        },
    ];

    const addNewDevDeviceWhitelist = () => {
        setFormData({imsi: '', reason: ''});
        setErrors({});
        setExpanderIsOpen(true);
    };

    const onImsiChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, imsi: value }));
    };

    const onReasonChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, reason: value }));
    };
    const onSubmit = async () => {
        const isValid = validateForm();
        if (!isValid) {
            return;
        }
        // API CALL
        try {
            await whitelistDevDevice([
              {
                imsi: formData.imsi!,
                reason: formData.reason!
              },
            ]).unwrap();

            setExpanderIsOpen(false);
          } catch (err) {
            console.error('Failed to whitelist device:', err);
          }
    };
    const onCancel = () => {
        setExpanderIsOpen(false);
    };

    const validateForm = () => {
        const newErrors: { imsi?: string; reason?: string } = {};

        if (!formData.imsi) {
            newErrors.imsi = formatMessage({
                id: 'intl-msg:coreconAdminWeb.devDeviceWhitelist.imsi.validation.missing',
            });
        } else {
            const imsiRegex = /^\d{15}$/;
            if (!imsiRegex.test(formData.imsi.trim())) {
                newErrors.imsi = formatMessage({
                    id: 'intl-msg:coreconAdminWeb.devDeviceWhitelist.imsi.validation.invalid',
                });
            }
        }

        if (!formData.reason) {
            newErrors.reason = formatMessage({
                id: 'intl-msg:coreconAdminWeb.devDeviceWhitelist.reason.validation.missing',
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    return (
        <>
            <div className="form-group">
                            <div className="display-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-default margin-right-10"
                                    onClick={addNewDevDeviceWhitelist}
                                >
                                    <FormattedMessage
                                        id={'intl-msg:coreconAdminWeb.devDeviceWhitelist.whitelist.device'} />
                                </button>
                            </div>
                        </div>
            <Collapse open={expanderIsOpen}>
                <div className="panel-wrapper margin-top-20 margin-bottom-20">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div className="margin-bottom-15">
                                <div className={'form-group' + `${errors.imsi ? ' has-feedback has-error' : ''}`}>
                                    <label htmlFor="imsi">
                                        <span>
                                            IMSI*
                                        </span>
                                    </label>
                                    <input
                                        className="form-control max-width-800"
                                        id="imsi"
                                        value={formData.imsi}
                                        onChange={onImsiChange}
                                    />
                                    {errors.imsi && (
                                        <span className="help-block margin-bottom-10">
                                            <span>{errors.imsi}</span>
                                        </span>
                                    )}
                                </div>
                                <div className={'form-group' + (errors.reason ? ' has-feedback has-error' : '')}>
                                    <label htmlFor="reason">
                                        <span>
                                            <FormattedMessage
                                                id={'intl-msg:coreconAdminWeb.devDeviceWhitelist.reason'} />*
                                        </span>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        rows={2}
                                        id="reason"
                                        value={formData.reason}
                                        tabIndex={3}
                                        onChange={onReasonChange}
                                    />
                                    {errors.reason && (
                                        <span className="help-block margin-bottom-10">
                                            <span>{errors.reason}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="display-flex justify-content-end margin-bottom-20">
                    <Button type="button" className="btn btn-default margin-right-10" onClick={onCancel}>
                        <FormattedMessage id={'intl-msg:coreconAdminWeb.devDeviceWhitelist.cancel'} />
                    </Button>
                    <Button type="button" className="btn btn-primary"
                        onClick={onSubmit} disabled={isLoadingDevDeviceWhitelistSubmit}>
                        <FormattedMessage id={'intl-msg:coreconAdminWeb.devDeviceWhitelist.whitelist'} />
                    </Button>
                </div>
            </Collapse>
            <h3><FormattedMessage id={'intl-msg:coreconAdminWeb.devDeviceWhitelist.whitelisted.devices'} /></h3>
            <ListTable
                items={devDevicesWhitelistList}
                isLoading={isLoadingDevDevicesWhitelistList}
                itemKey="imsi"
                showHeader
                columnDescriptors={columnDescriptors}
            />
        </>
    );
};